/**
 * overlapReportModel — turns the offset engine's overlap map into something an
 * operator can read while standing at a machine-room laptop.
 *
 * `detectMatrixOverlaps` answers "is this cell red?", and it answers it by
 * filing ONE `OverlapInfo` under two keys — `${a.colKey}:${a.unitIdx}` and
 * `${b.colKey}:${b.unitIdx}` — so that both colliding cells can look themselves
 * up. Those two keys are always distinct: two addresses of the same column
 * differ in their unit, and two addresses of the same unit differ in their
 * column. Flattening the map therefore reports every collision exactly twice,
 * and a report that lists a fault twice is a report an operator stops trusting
 * on the second screen.
 *
 * The shape here is two levels because the repair has two levels:
 *
 *   - a FINDING is one unordered pair of columns that collide anywhere. That is
 *     the unit of repair: one finding is one matrix cell to go and change.
 *   - an OCCURRENCE is one concrete collision inside that finding — which two
 *     cars, and which bytes they both claim. Six identical-shaped occurrences
 *     under one finding is how the report says "the initial offset is wrong",
 *     as against one occurrence, which says "the delta is".
 *
 * Nothing here imports React or antd: the same report feeds the in-app panel
 * and the printable window, and one model means the two cannot disagree.
 */
import type { OverlapInfo, ResolvedAddress } from "./offsetOverlapDetection";

/**
 * Cars are 1-based on screen; the offset engine's `unitIdx` is 0-based.
 *
 * This is the single conversion point. Every car number the report shows — in
 * the panel and in the printable document alike — comes through here, so there
 * is no second site at which a `+ 1` could be added twice or forgotten once.
 * The matrix tooltip renders the same number and calls it "lift N"; the noun
 * differs because this screen already says "Car Delta" and `numCars`, but the
 * number does not.
 *
 * `null` is the answer for `SHARED_UNIT` — a bank-level, group-shared or
 * zero-delta register, which is one register the whole bank reads. There is no
 * car number to give, and inventing one is how a single shared register came to
 * be reported as N collisions between cars that own no register at all.
 */
export function carNumber(unitIdx: number): number | null {
  if (unitIdx < 0) return null;
  return unitIdx + 1;
}

/** `Car 3`, or `All cars` for a register the whole bank shares. */
export function formatCarLabel(car: number | null): string {
  if (car === null) return "All cars";
  return `Car ${car}`;
}

/** One concrete collision: two cars, two byte ranges, and what they share. */
export interface OverlapOccurrence {
  /** 1-based car number of the side-A register; `null` when it is shared. */
  carA: number | null;
  /** 1-based car number of the side-B register; `null` when it is shared. */
  carB: number | null;
  /**
   * The engine's own 0-based unit index for side A, carried unconverted.
   *
   * Kept deliberately: a test that asserts `carA === unitAIdx + 1` can only be
   * written while both numbers survive, and collapsing to the 1-based value
   * alone would remove the ability to check the mapping at all.
   */
  unitAIdx: number;
  /** The engine's own 0-based unit index for side B, carried unconverted. */
  unitBIdx: number;
  /** First byte of the side-A register, inclusive. */
  aStart: number;
  /** One past the last byte of the side-A register. */
  aEnd: number;
  /** First byte of the side-B register, inclusive. */
  bStart: number;
  /** One past the last byte of the side-B register. */
  bEnd: number;
  /** Every byte both registers claim, ascending and contiguous. */
  bytes: number[];
  /** True when the two registers belong to different cars. */
  crossUnit: boolean;
}

/** One pair of columns that collide, and every collision between them. */
export interface OverlapFinding {
  /** Column key of the A side, always the lexicographically smaller of the two. */
  aColKey: string;
  /** Column key of the B side. */
  bColKey: string;
  /** Human label of the A-side column, as the matrix header renders it. */
  aLabel: string;
  /** Human label of the B-side column, as the matrix header renders it. */
  bLabel: string;
  /** Collisions between these two columns, cross-car first. */
  occurrences: OverlapOccurrence[];
  /** How many of `occurrences` span two different cars. */
  crossUnitCount: number;
  /** True when any occurrence spans two different cars. */
  hasCrossUnit: boolean;
  /** The lowest byte contended anywhere in this finding. */
  firstContendedByte: number;
}

/** Everything the overlap analysis found, plus the scope it looked at. */
export interface OverlapReport {
  /** Column pairs that collide, most dangerous first. */
  findings: OverlapFinding[];
  /** Total collisions across every finding — the number of rows rendered. */
  totalOccurrences: number;
  /** How many of those collisions span two different cars. */
  crossUnitOccurrences: number;
  /**
   * The bytes two different cars both claim, ascending and deduplicated.
   *
   * Named separately from the counts because this is the list an operator has
   * to act on: every one of these is a byte whose telemetry publishes under the
   * wrong car, and each is fixed by moving a car base until the strides stop
   * crossing. The server reports the same bytes on every CDO build.
   */
  contestedBytes: number[];
  /** Cars the detection ran over. Below 2, cross-car bleed cannot be seen. */
  carsAnalyzed: number;
  /** Columns the detection ran over. Zero means nothing was checked. */
  columnsChecked: number;
  /**
   * Anything the operator needs to know about the scope itself, or `null`.
   *
   * The report cannot work out on its own where its car count came from — the
   * caller does. This is how "the form says 8 cars and the bank has 2" reaches
   * the screen instead of being silently resolved into a number the operator
   * then has no way to question.
   */
  scopeNote: string | null;
}

/** The scope a report was computed over, stated so an empty one is provable. */
export interface OverlapReportScope {
  /** Cars the detection ran over. */
  carsAnalyzed: number;
  /** Columns the detection ran over. */
  columnsChecked: number;
  /** Optional caveat about that scope, shown as a warning where it exists. */
  scopeNote?: string | null;
}

/** `[168, 184)` — the half-open form the matrix tooltip already prints. */
export function formatRegisterRange(start: number, end: number): string {
  return `[${start}, ${end})`;
}

/** `177-183 (7)`, or `184 (1)` — inclusive, because these are real byte numbers. */
export function formatContendedBytes(bytes: readonly number[]): string {
  if (bytes.length === 0) return "none";
  if (bytes.length === 1) return `${bytes[0]} (1)`;
  return `${bytes[0]}-${bytes[bytes.length - 1]} (${bytes.length})`;
}

/**
 * The word the report repeats in every row's Scope column.
 *
 * Three values, not two. "shared" is the case where one of the two registers
 * belongs to the whole bank rather than to a car: a real collision, but not the
 * cross-car kind, and calling it CROSS-CAR would attach the report's loudest
 * marker to a register that has no car to bleed into.
 */
export function scopeLabel(
  occurrence: Pick<OverlapOccurrence, "crossUnit" | "carA" | "carB">,
): string {
  if (occurrence.crossUnit) return "CROSS-CAR";
  if (occurrence.carA === null || occurrence.carB === null) return "shared";
  return "same car";
}

/**
 * Every distinct `OverlapInfo` in the map, each seen once.
 *
 * Identity is exact rather than value-hashed: the map holds the SAME object
 * reference under both of its keys, so a `Set` collapses the double-filing
 * without needing a canonical key, and without any risk of merging two
 * genuinely different collisions that happen to hash alike.
 */
function distinctInfos(overlapMap: ReadonlyMap<string, OverlapInfo[]>): OverlapInfo[] {
  const seen = new Set<OverlapInfo>();
  overlapMap.forEach((infos) => {
    infos.forEach((info) => seen.add(info));
  });
  return [...seen];
}

/**
 * Put the pair in a fixed order so one finding always shows the same column on
 * the A side.
 *
 * `detectMatrixOverlaps` happens to emit a stable orientation today, because it
 * scans an upper triangle over addresses built column by column. Relying on
 * that would make this file's output depend on an invariant three functions
 * away; normalising locally makes the A side deterministic by construction.
 */
function orient(info: OverlapInfo): { a: ResolvedAddress; b: ResolvedAddress } {
  return info.a.colKey <= info.b.colKey ? { a: info.a, b: info.b } : { a: info.b, b: info.a };
}

/** A shared register sorts ahead of car 1, which is where it is read. */
function carSortKey(car: number | null): number {
  if (car === null) return 0;
  return car;
}

/** Cross-car first, then by lowest contended byte, then by car. */
function compareOccurrences(x: OverlapOccurrence, y: OverlapOccurrence): number {
  if (x.crossUnit !== y.crossUnit) return x.crossUnit ? -1 : 1;
  const xLo = x.bytes[0] ?? 0;
  const yLo = y.bytes[0] ?? 0;
  if (xLo !== yLo) return xLo - yLo;
  const carA = carSortKey(x.carA) - carSortKey(y.carA);
  if (carA !== 0) return carA;
  return carSortKey(x.carB) - carSortKey(y.carB);
}

/** Cross-car findings first, then by lowest contended byte, then by label. */
function compareFindings(x: OverlapFinding, y: OverlapFinding): number {
  if (x.hasCrossUnit !== y.hasCrossUnit) return x.hasCrossUnit ? -1 : 1;
  if (x.firstContendedByte !== y.firstContendedByte) {
    return x.firstContendedByte - y.firstContendedByte;
  }
  const byA = x.aLabel.localeCompare(y.aLabel);
  return byA !== 0 ? byA : x.bLabel.localeCompare(y.bLabel);
}

/**
 * Build the operator-facing report from the raw detection map.
 *
 * The map is the one the offset matrix paints its red cells from, so a report
 * built here cannot list a collision the matrix does not show, nor miss one it
 * does.
 */
export function buildOverlapReport(
  overlapMap: ReadonlyMap<string, OverlapInfo[]>,
  scope: OverlapReportScope,
): OverlapReport {
  const byPair = new Map<string, OverlapFinding>();

  distinctInfos(overlapMap).forEach((info) => {
    const { a, b } = orient(info);
    const key = `${a.colKey}|${b.colKey}`;
    const occurrence: OverlapOccurrence = {
      carA: carNumber(a.unitIdx),
      carB: carNumber(b.unitIdx),
      unitAIdx: a.unitIdx,
      unitBIdx: b.unitIdx,
      aStart: a.absByte,
      aEnd: a.absByte + a.size,
      bStart: b.absByte,
      bEnd: b.absByte + b.size,
      bytes: [...info.overlappingBytes],
      crossUnit: info.crossUnit,
    };
    const existing = byPair.get(key);
    if (existing) {
      existing.occurrences.push(occurrence);
      return;
    }
    byPair.set(key, {
      aColKey: a.colKey,
      bColKey: b.colKey,
      aLabel: a.label,
      bLabel: b.label,
      occurrences: [occurrence],
      crossUnitCount: 0,
      hasCrossUnit: false,
      firstContendedByte: 0,
    });
  });

  const findings = [...byPair.values()].map((finding) => {
    const occurrences = [...finding.occurrences].sort(compareOccurrences);
    const crossUnitCount = occurrences.filter((o) => o.crossUnit).length;
    const lowest = occurrences.reduce(
      (lo, o) => Math.min(lo, o.bytes[0] ?? lo),
      Number.MAX_SAFE_INTEGER,
    );
    return {
      ...finding,
      occurrences,
      crossUnitCount,
      hasCrossUnit: crossUnitCount > 0,
      firstContendedByte: lowest === Number.MAX_SAFE_INTEGER ? 0 : lowest,
    };
  });

  findings.sort(compareFindings);

  return {
    findings,
    totalOccurrences: findings.reduce((n, f) => n + f.occurrences.length, 0),
    crossUnitOccurrences: findings.reduce((n, f) => n + f.crossUnitCount, 0),
    contestedBytes: [
      ...new Set(
        findings.flatMap((f) => f.occurrences.filter((o) => o.crossUnit).flatMap((o) => o.bytes)),
      ),
    ].sort((x, y) => x - y),
    carsAnalyzed: scope.carsAnalyzed,
    columnsChecked: scope.columnsChecked,
    scopeNote: scope.scopeNote ?? null,
  };
}

/** `1 column`, `4 columns` — this document gets printed and signed off. */
function plural(count: number, singular: string): string {
  if (count === 1) return `${count} ${singular}`;
  return `${count} ${singular}s`;
}

/** `4 columns x 6 cars checked` — the scope clause, on its own. */
export function formatScope(report: OverlapReport): string {
  return `${plural(report.columnsChecked, "column")} x ${plural(
    report.carsAnalyzed,
    "car",
  )} checked`;
}

/**
 * The one-line summary both the panel and the printable document open with.
 *
 * "No overlapping offsets" is withheld when nothing was compared. Zero columns
 * is not a clean adapter, it is a check that did not run, and leading with the
 * affirmative sentence is how an operator reads a scope of zero as a pass.
 */
export function summarizeOverlapReport(report: OverlapReport): string {
  const scope = formatScope(report);
  if (report.columnsChecked === 0) {
    return `No offsets were checked. ${scope}.`;
  }
  if (report.findings.length === 0) {
    return `No overlapping offsets. ${scope}.`;
  }
  const offsets = report.totalOccurrences === 1 ? "overlapping offset" : "overlapping offsets";
  const pairs = report.findings.length === 1 ? "register pair" : "register pairs";
  return (
    `${report.totalOccurrences} ${offsets} in ${report.findings.length} ${pairs}, ` +
    `${report.crossUnitOccurrences} cross-car, ${scope}`
  );
}

/** Stated whenever cross-car bleed could not have been seen at all. */
export const SINGLE_CAR_WARNING =
  "Only 1 car is configured, so cross-car overlaps cannot be detected. " +
  "Set the car count to check them.";

/**
 * Stated whenever the run compared nothing at all.
 *
 * Zero columns happens two ways: an adapter with no key mappings yet, so no
 * column has a byte size to compare; and a protocol whose offsets carry no size
 * information for this check to work from — Modbus renders an editable matrix
 * but its register spaces have no per-category width, so nothing can be ranged.
 * Either way the honest answer is "this did not look", never "nothing found".
 */
export const NOTHING_CHECKED_WARNING =
  "No offset columns were analysed, so this report neither confirms nor denies " +
  "an overlap. Column sizes come from the adapter's key mappings, and this " +
  "adapter has none the check can use.";

/**
 * One line naming the bytes two cars both claim, or `null` when there are none.
 *
 * Written here rather than in the editor so the wording is the same wherever it
 * is shown, and so the "is there anything to say" decision has one home. The
 * byte list is capped: a mapping whose strides cross badly can contest dozens,
 * and a banner that runs off the screen is one nobody finishes reading.
 */
export function contestedByteMessage(report: OverlapReport): string | null {
  if (report.contestedBytes.length === 0) return null;

  const shown = report.contestedBytes.slice(0, 8);
  const rest = report.contestedBytes.length - shown.length;
  const list = shown.join(", ") + (rest > 0 ? `, and ${rest} more` : "");
  const byteWord = report.contestedBytes.length === 1 ? "byte is" : "bytes are";

  return (
    `${report.contestedBytes.length} ${byteWord} claimed by two different cars: ${list}. ` +
    "A wire byte belongs to one car, so one car's telemetry will publish under " +
    "another car's name. Open the Overlap Analysis to see which cars, and move a " +
    "car's base offset until the strides stop crossing."
  );
}
